import {
  Context,
  Dispatch,
  memo,
  MemoExoticComponent,
  Provider,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';
import {Form} from 'react-final-form';

import FormField from '~/shared/components/FormField';
import useFormAutoFocus from '~/shared/hooks/useFormAutoFocus';
import {
  FieldWrapper,
  FormWrapper,
  HeaderText,
  LoginFormFieldsWrapper,
  SubmitButton,
  Email,
  BlueLinkButton,
  LinkButtonsWrapper,
  ReSendCodeIndication,
  ResendResError,
} from '~/shared/components/loginStyled';
import useCanResendCode from '~/shared/utils/useCanResendCode';
import {getLocalizationService} from '~/shared/services/localisationService';

import {VerificationFormContext, VerificationFormContextProps} from './context';

const StyledFormField = styled(FormField)<{isGov?: boolean}>`
  ${({isGov}) => isGov && `
    height: auto;
    margin-bottom: 32px;
  `}
`;

const StyledFieldWrapper = styled(FieldWrapper)`
  margin-top: 32px;
`;

const StyledLinkButtonsWrapper = styled(LinkButtonsWrapper)`
  margin-top: 26px;
`;

const formValidation = (values: {authenticationCode?: string}) => {
  if (values?.authenticationCode?.length !== 5) {
    return {authenticationCode: 'activation_code_must_be_exact_5_digits'};
  }

  return undefined;
};

interface IVerificationForm {
  onReSendCode: () => void;
  onSubmit: ({authenticationCode}: {authenticationCode?: string}) => void;
  email?: string;
  headerText: ReactNode;
  resErrors: string | null;
  setResErrors: Dispatch<SetStateAction<string | null>>;
  toUpdatePhoneNumberScreen?: () => void;
  isGov?: boolean;
  className?: string;
}

const VerificationForm = ({
  onReSendCode,
  onSubmit,
  email,
  headerText,
  resErrors: resErrorFromServer,
  setResErrors,
  toUpdatePhoneNumberScreen,
  isGov,
  className,
}: IVerificationForm) => {
  const {t} = getLocalizationService();
  const [isTriedReSendingCode, setIsTriedReSendingCode] = useState<boolean | null>(false);
  const {isResendButtonDisabled, setIsResendButtonDisabled} = useCanResendCode();

  const resetCode = () => {
    if (isResendButtonDisabled) {
      return;
    }

    setIsTriedReSendingCode(true);
    onReSendCode();
    setIsResendButtonDisabled(true);
  };

  const {userError, isRequireCaptcha} = useContext(VerificationFormContext);
  const resErrorDescription = useMemo(() => userError?.message || resErrorFromServer, [resErrorFromServer, userError]);
  const formWrapRef = useFormAutoFocus();
  return (
    <div className={className}>
      <Form
        onSubmit={onSubmit}
        validateOnBlur
        validate={formValidation}
        render={({handleSubmit}) => (
          <FormWrapper onSubmit={handleSubmit} ref={formWrapRef}>
            {!isGov && <Email className="verification-form-email">{email}</Email>}
            {!isGov && <HeaderText className="verification-form-header">{headerText}</HeaderText>}
            <LoginFormFieldsWrapper>
              <StyledFieldWrapper isGov={isGov}>
                <StyledFormField
                  {...{
                    name: 'authenticationCode',
                    placeholder: t('insert_the_code'),
                    type: 'text',
                    required: true,
                    className: 'white-border-on-contrast',
                    shouldUseMetaModified: true,
                    withBorder: isGov,
                    isGov,
                    inputProps: {
                      onKeyUp: () => {
                        if (!resErrorDescription) {
                          return;
                        }
                        setIsTriedReSendingCode(null);
                        setResErrors(null);
                      },
                    },
                  }}
                />
              </StyledFieldWrapper>
              {/* @ts-expect-error TODO: refactor FormField to TS*/}
              {isRequireCaptcha && <FormField name="reCaptchaResponse" type="recaptcha" />}
            </LoginFormFieldsWrapper>
            <SubmitButton className="confirm-button" type="submit">{t('accept')}</SubmitButton>
          </FormWrapper>
        )}
      />
      <StyledLinkButtonsWrapper isGov={isGov}>
        {toUpdatePhoneNumberScreen && (
          <BlueLinkButton isGov={isGov} onClick={toUpdatePhoneNumberScreen}>{t('update_phone_number')}</BlueLinkButton>
        )}
        <BlueLinkButton className="resend-code-button" isGov={isGov} onClick={resetCode} isDisabled={isResendButtonDisabled}>
          {t('i_did_not_receive_send_again')}
        </BlueLinkButton>
        {resErrorDescription && <ResendResError>{resErrorDescription}</ResendResError>}
        {isResendButtonDisabled && isTriedReSendingCode && !resErrorDescription && (
          <ReSendCodeIndication>{t('we_sent_you_a_new_code')}</ReSendCodeIndication>
        )}
      </StyledLinkButtonsWrapper>
    </div>
  );
};

const PureVerificationForm = memo(VerificationForm) as MemoExoticComponent<
  (props: IVerificationForm) => JSX.Element | null
> & {
  Provider: Provider<VerificationFormContextProps>;
  Context: Context<VerificationFormContextProps>;
};

PureVerificationForm.Context = VerificationFormContext;
PureVerificationForm.Provider = VerificationFormContext.Provider;

export default PureVerificationForm;
